import type {
    ViolationDetailPayload,
    ViolationDetailResponse,
} from '@/types/apps/violation-detail.type';

import type {
    ApiResponse,
    PaginatedResult,
    QueryParams,
} from '@/types/common/api.type';
import type { BaseApiClientInterface } from '@/types/service/base-api-client.interface';
import type { Creatable } from '@/types/service/creatable.interface';
import type { Deletable } from '@/types/service/deletable.interface';
import type { Exportable } from '@/types/service/exportable.interface';
import type { Importable } from '@/types/service/importable.interface';
import type { Indexable } from '@/types/service/indexable.interface';
import type { Showable } from '@/types/service/showable.interface';
import type { Updatable } from '@/types/service/updatable.interface';

type ViolationDetailListResponse = ApiResponse<
    PaginatedResult<ViolationDetailResponse>
>;

export class ViolationDetailService
    implements
        Showable,
        Updatable,
        Deletable,
        Creatable,
        Indexable,
        Importable,
        Exportable
{
    public constructor(private apiClient: BaseApiClientInterface) {}

    protected endpoints = {
        base: () => '/api/ViolationDetails',
        detail: (id: string) => `/api/ViolationDetails/${id}`,
        import: () => '/api/ViolationDetails/import',
        export: () => '/api/ViolationDetails/export',
    };

    public index(params?: QueryParams): Promise<ViolationDetailListResponse> {
        return this.apiClient.fetchApi(this.endpoints.base(), params);
    }

    public detail(
        id: string,
        params?: QueryParams
    ): Promise<ApiResponse<ViolationDetailResponse>> {
        const url = this.endpoints.detail(id);

        return this.apiClient.fetchApi(url, params);
    }

    public create(
        payload: ViolationDetailPayload
    ): Promise<ApiResponse<ViolationDetailResponse>> {
        return this.apiClient.postApi(this.endpoints.base(), payload);
    }

    public update(
        id: string,
        payload: ViolationDetailPayload
    ): Promise<ApiResponse<ViolationDetailResponse>> {
        return this.apiClient.putApi(this.endpoints.detail(id), payload);
    }
    public destroy(
        ids: string[],
        isHardDeleted: boolean = false
    ): Promise<ApiResponse<void>> {
        const url = this.endpoints.base();

        return this.apiClient.destroyApi<void>(url, ids, isHardDeleted);
    }

    public async importFile(file: File): Promise<any> {
        const form = this.apiClient.buildFormData({ file });

        return this.apiClient.postApi<any>(this.endpoints.import(), form);
    }

    public async exportFile(): Promise<Blob> {
        return this.apiClient.fetchBlob(this.endpoints.export());
    }
}
